import { Component, Input, ViewChild, ViewContainerRef, ComponentRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, combineLatest, of } from 'rxjs';
import { Thesaurus } from '@interfaces/thesaurus.interface';
import { ThesaurusService } from '@thesaurus-service/thesaurus.service';
import { ThesaurusDetailComponent } from '@thesaurus-detail/thesaurus-detail.component';
import { TermsService } from '@terms-service/terms.service';
import { EntryService } from 'app/services/entry/entry.service';
import { ThesaurusSingleTermComponent } from '@thesaurus-single-term/thesaurus-single-term.component';
import { DetailContext } from '@interfaces/detail-context.interface';
import { ThesaurusHierarchicalViewComponent } from '@thesaurus-hierarchical-view/thesaurus-hierarchical-view.component';
import { WebsocketService } from '@websocket-service/websocket.service';
import { PayloadService } from '@payload-service/payload.service';
import { ThesaurusSelectedTerm } from '@interfaces/thesaurus-selected-term.interface';
import { ThesaurusRootComponent } from '../thesaurus-root/thesaurus-root.component';
import { getAppendToField, getClosePopup, getProxyRedirect} from 'app/app.module';
import { UtilitiesService } from '@utilities-service/utilities.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { EventsService } from 'app/services/events/events.service';

@Component({
    selector: 'app-thesaurus-terms',
    templateUrl: './thesaurus-terms.component.html',
    styleUrl: './thesaurus-terms.component.scss',
    standalone: false
})
export class ThesaurusTermsComponent {
  @ViewChild('thesaurusDetailContainer', { read: ViewContainerRef }) container!: ViewContainerRef;
  @ViewChild('thesaurusSingleTermContainer', { read: ViewContainerRef }) thesaurusSingleTermContainer!: ViewContainerRef;
  @ViewChild('thesaurusHierarchicalViewContainer', { read: ViewContainerRef }) thesaurusHierarchicalViewContainer!: ViewContainerRef;
  @ViewChild('thesaurusRootContainer', { read: ViewContainerRef }) thesaurusRootContainer!: ViewContainerRef;

  @Input() thesaurus?: Thesaurus;
  @Input() token!: string;
  @Input() term?: string;
  @Input() searchTerm?: string;
  @Input() searchOption?: string;
  @Input() searchCategory?: string;
  @Input() searchType?: string;
  thesauri: Thesaurus[] = [];
  termTest?: string;
  detailComponentContext: DetailContext = { searchTerm: '', browseTerm: '', searchOption: '', searchCategory: '', searchType: '', offset: 0 };
  subscription: any;
  private thesaurusDetailComponentRef?: ComponentRef<ThesaurusDetailComponent>;
  private thesaurusSingleTermComponentRef?: ComponentRef<ThesaurusSingleTermComponent>;
//  private thesaurusHierarchicalViewComponentRef?: ComponentRef<ThesaurusHierarchicalViewComponent>;
  private thesaurusRootComponentRef?: ComponentRef<ThesaurusRootComponent>;
  display: boolean = true;
  displayFooter: boolean = false;
  displayPagination: boolean = false;
    productTypes: { name: string, mjSubject: string, nonMjSubject: string }[] = [
    { name: 'MEDLINE', mjSubject: 'MJMESH', nonMjSubject: 'MESH' },
    { name: 'EMBASE', mjSubject: 'MJEMB', nonMjSubject: 'EMB' },
    { name: 'MLA', mjSubject: 'MLASU', nonMjSubject: 'MLASU' },
    { name: 'COMPANY', mjSubject: 'CORP', nonMjSubject: 'CORP' },
    { name: 'LEXICONTERM', mjSubject: 'MJMAINSUBJECT', nonMjSubject: 'MAINSUBJECT' },
    { name: 'OTHERS', mjSubject: 'MJMAINSUBJECT', nonMjSubject: 'MAINSUBJECT' }
  ];
    disabledClass: string = 'disabled';
    prevPageBtnID: string = "previousPageButton";
    closePopup: boolean = true; //Used to determine if the modal should be closed after a thesaurus entry is selected
    appendField: boolean = false;
    targetFieldName: string = ''; //Used to determine the target field for thesaurus entries
    proxyRedirect: boolean = true;

  constructor(
    private thesaurusService: ThesaurusService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    public termsService: TermsService,
    private entryService: EntryService,
    private websocketService: WebsocketService,
    private payloadService: PayloadService,
    private utilitiesService: UtilitiesService,
    private eventsService: EventsService
  ) {}

  ngOnInit(): void {
    this.closePopup = getClosePopup(this.route.snapshot.queryParams['amp;closePopup']);
    this.appendField = getAppendToField(this.route.snapshot.queryParams['amp;appendToField']);
    this.proxyRedirect = getProxyRedirect(this.route.snapshot.queryParams['amp;proxyRedirect']);
    this.targetFieldName = this.route.snapshot.queryParams['amp;targetFieldName'] || '';
       

    this.entryService.getEntryClickEvent().subscribe((event) => {
      if (event) {
        this.handleEntryClick(event.thesaurusId, event.entry);
      }
    }
    );
    this.entryService.getBackLinkClickEvent().subscribe(() => {
      if (this.container) {
        this.createThesaurusDetailComponentFromContext();
      }
    });

    this.subscription = combineLatest([this.route.params, this.route.queryParams])
      .subscribe(([params, queryParams]) => {
        this.token = queryParams['token'];
        this.termTest = params['availableTerm'];
        this.setThesaurus(params['thesaurusId'], params['availableTerm'], queryParams['token'], queryParams['selecteddatabases'], queryParams['thesaurus']);
      });
  }

  ngOnDestroy(): void {
    //Clear any "Selected Terms" when a user switches to a different thesaurus
    this.termsService.clearList();
    this.termsService.clearEntries();
    this.subscription.unsubscribe();

    // Reset detailComponentContext properties to null to ensure full cleanup
    this.detailComponentContext.searchTerm = null as any;
    this.detailComponentContext.browseTerm = null as any;
    this.detailComponentContext.searchOption = null as any;
    this.detailComponentContext.searchCategory = null as any;
    this.detailComponentContext.searchType = null as any;
    this.detailComponentContext.offset = null as any;
    this.detailComponentContext = null as any;

    // Clear all containers first to remove all dynamic components
    if (this.container) {
      this.container.clear();
    }
    if (this.thesaurusDetailComponentRef) {
      this.thesaurusDetailComponentRef.destroy();
      this.thesaurusDetailComponentRef = undefined;
    }
    this.cdr.detectChanges();
  }

  isExplode(): boolean {
    if (!this.thesaurus) {
      return false;
    }
    if (this.thesaurus.explode) {
      let thesWithExplode: number = 0;
      if (Array.isArray(this.thesauri) && this.thesauri.length > 1) {
        for (let thes of this.thesauri) {
          if (thes.explode) {
            thesWithExplode++;
          }
        }
        // if more than one thesaurus has explode set to true, don't display the checkbox
        return thesWithExplode == 1 ? true : false;
      }
      else if (this.thesaurus) {
        return this.thesaurus.explode;
      }
    }
    return false;
  }

  isMajor(): boolean {
    return this.thesaurus ? this.thesaurus.major : false;
  }

  showNoFrillsLexicon() {
    return this.utilitiesService.getLookupQueryParam();
  }

  handleEntryClick(thesaurusId: string, entry: number) {
    this.showIndexSpinner();
    if (this.thesaurusDetailComponentRef) {
      this.saveDetailComponentContext(this.thesaurusDetailComponentRef.instance);
      this.thesaurusDetailComponentRef.destroy();
    }
    // Destroy existing instance of thesaurusSingleTermComponentRef if it exists
    if (this.thesaurusSingleTermComponentRef) {
      this.thesaurusSingleTermComponentRef.destroy();
    }

    // if (this.thesaurusHierarchicalViewComponentRef) {
    //   this.thesaurusHierarchicalViewComponentRef.destroy();
    // }

    if(this.thesaurusRootComponentRef) {
      this.thesaurusRootComponentRef.destroy();
    }

    //Create a new instance of ThesaurusDetailComponent
    this.thesaurusSingleTermComponentRef = this.thesaurusSingleTermContainer.createComponent(ThesaurusSingleTermComponent);
    this.thesaurusSingleTermComponentRef.instance.thesauri = this.thesauri;
    this.thesaurusSingleTermComponentRef.instance.thesaurus = this.thesaurus;
    this.thesaurusSingleTermComponentRef.instance.token = this.token;
    this.thesaurusSingleTermComponentRef.instance.entryOrdinal = entry;
    this.thesaurusSingleTermComponentRef.instance.thesaurusId = thesaurusId;

    // this.thesaurusHierarchicalViewComponentRef = this.thesaurusHierarchicalViewContainer.createComponent(ThesaurusHierarchicalViewComponent);
    // this.thesaurusHierarchicalViewComponentRef.instance.thesauri = this.thesauri;
    // this.thesaurusHierarchicalViewComponentRef.instance.thesaurus = this.thesaurus;
    // this.thesaurusHierarchicalViewComponentRef.instance.token = this.token;
    // this.thesaurusHierarchicalViewComponentRef.instance.thesaurusId = thesaurusId;
    // this.thesaurusHierarchicalViewComponentRef.instance.entryOrdinal = entry;
    
    // Manually trigger change detection
    this.cdr.detectChanges();
  }

  saveDetailComponentContext(thesaurusDetailComponent: ThesaurusDetailComponent) {
    this.detailComponentContext.searchTerm = thesaurusDetailComponent.searchTerm || '';
    this.detailComponentContext.browseTerm = thesaurusDetailComponent.term || '';
    this.detailComponentContext.searchOption = thesaurusDetailComponent.searchOption || '';
    this.detailComponentContext.offset = thesaurusDetailComponent.offset || 0;

    this.detailComponentContext.searchCategory = thesaurusDetailComponent.searchCategory || '';
    this.detailComponentContext.searchType = thesaurusDetailComponent.searchType || '';
  }

  //When a "Browse Term" is clicked on in the thesaurus-index child component, instantiate a new instance of the thesaurus-detail child component.
  onBrowseTermsClick(event: { thesaurusId: string, availableTerm: string }) {
    if (this.display) {
      this.display = false;
    }
    this.displayFooter = true;
    if (event.availableTerm) {
      this.term = event.availableTerm;
    }

    this.termsService.resetOffset();
    if (this.container) {
      if (event.availableTerm == 'Root') {
        this.createThesaurusRootComponent();
      } else {
        this.createThesaurusDetailComponent(this.term ?? '', this.searchTerm ?? '', this.searchOption ?? '', 0, this.searchCategory ?? '', this.searchType ?? '');
      }
    }
  }

  onBrowseCategoriesClick(event: { thesaurusId: string, availableCategory: string }) {
    if (this.display) {
      this.display = false;
    }
    this.displayFooter = true;
    if (event.availableCategory) {
      this.searchCategory = event.availableCategory;
    }

    this.termsService.resetOffset();
    this.createThesaurusDetailComponent('', '', '', 0, this.searchCategory ?? '', '');
  }

  findSearchTerm(event: { thesaurusId: string, searchTerm: string, searchOption: string, searchCategory?: string, searchType?: string }) {
    if (this.display) {
      this.display = false;
    }

    this.displayFooter = true;
    
    if (event.searchCategory) {
      this.searchCategory = event.searchCategory;
    }

    if (event.searchType) {
      this.searchType = event.searchType;
    }

    //For Lexicon: allow searching based on category and empty input
    if ((this.thesaurusService.isLexiconThesaurus(event.thesaurusId) || this.thesaurusService.isCorporationThesaurus(event.thesaurusId)) && (event.searchTerm === "")) {
      event.searchTerm = "*";
    }

    if (event.searchTerm) {
      this.searchTerm = event.searchTerm;
      this.term = undefined;
    }

    if (event.searchOption) {
      this.searchOption = event.searchOption;
      this.termsService.resetOffset();
      if (this.container) {
        this.createThesaurusDetailComponent(this.term ?? '', this.searchTerm ?? '', this.searchOption ?? '', 0, this.searchCategory ?? '', this.searchType ?? '');
      }
    }
  }

  onClearSearchTermsClick(event: { thesaurusId: string}) {
    this.display = true;
    this.displayFooter = false;
    this.term = undefined;
    this.searchTerm = undefined;
    this.searchOption = undefined;
    this.searchCategory = undefined;
    this.searchType = undefined;
    if (this.thesaurusDetailComponentRef) {
      this.thesaurusDetailComponentRef.destroy();
    }
    if (this.thesaurusSingleTermComponentRef) {
      this.thesaurusSingleTermComponentRef.destroy();
    }
    // if (this.thesaurusHierarchicalViewComponentRef) {
    //   this.thesaurusHierarchicalViewComponentRef.destroy();
    // }
    if (this.thesaurusRootComponentRef) {
      this.thesaurusRootComponentRef.destroy();
    }
  }

  getProduct(thesaurusName: string): any {
      if (thesaurusName.toLowerCase().indexOf("medline") !== -1) {
        return this.productTypes[0];
      }
      if (thesaurusName.toLowerCase().indexOf("embase") !== -1) {
        return this.productTypes[1];
      }
      if (thesaurusName.toLowerCase().indexOf("mla") !== -1) {
        return this.productTypes[2];
      }
      if (thesaurusName.toLowerCase().indexOf("company") !== -1) {
        return this.productTypes[3];
      }
      if (thesaurusName.toLowerCase().indexOf("acta_lexicon_qualifiers_en") !== -1) {
        return this.productTypes[4];
      }
      return this.productTypes[5];
    }
  
    private getMnemonic(term: ThesaurusSelectedTerm): string {
      let product = this.getProduct(this.thesaurus?.id? this.thesaurus.id: "");
      if (term.majorChecked) {
        return product.mjSubject;
      }
      return product.nonMjSubject;
    
    }

  async submitThesaurusEntries(event: { event: Event, joinType: string }): Promise<void> {
    if (this.termsService.getTermCount() > 0) {  
      const allEntryNames = this.termsService.getAllCheckedEntryNames();

      //Remove any qualifiers that have been unchecked.
      const cleanedEntries = allEntryNames.map(entry => ({...entry, 
        qualifiers: entry.qualifiers ? entry.qualifiers.filter(q => q.qualifierChecked) : entry.qualifiers}));
      
      //Ensure that each entry has at least 1 checkbox checked, before passing the entry list to the payload service
      const filteredEntries = cleanedEntries.filter(entry => entry.mainChecked || entry.explodeChecked || entry.majorChecked || entry.relatedChecked || (entry.qualifiers && entry.qualifiers.some(q => q.qualifierChecked)));

      // An entry is eligible for name-based deduplication only when it has no special
      // search modifiers (explode, related, major) and no qualifiers. Entries with any
      // of these are intentionally distinct even if they share the same name.
      const shouldDeduplicateByName = (selectedEntry: ThesaurusSelectedTerm): boolean =>
        ((selectedEntry.paths && selectedEntry.paths.length > 0 )? 
	      (!(selectedEntry.relatedChecked)
        && (selectedEntry.paths[0].tn ? !selectedEntry.explodeChecked : true)
        && (!selectedEntry.qualifiers || selectedEntry.qualifiers.length === 0)) : 
	      (!selectedEntry.explodeChecked
        && !selectedEntry.relatedChecked
        && !selectedEntry.majorChecked
        && (!selectedEntry.qualifiers || selectedEntry.qualifiers.length === 0)));

      // Remove duplicate entries that share the same name, but only for entries eligible
      // for name-based deduplication. Ineligible entries (those with explode, related, major,
      // or qualifiers) are always kept as-is, since their modifiers make them semantically
      // distinct. For eligible entries, only the first occurrence of each name is retained.
      const deduplicatedEntries = filteredEntries.filter((entry, index, self) => {
        if (!shouldDeduplicateByName(entry)) {
          return true;
        }

        return index === self.findIndex((e) =>
          e.name === entry.name && shouldDeduplicateByName(e)
        );
      });

      if(this.proxyRedirect) {
        this.websocketService.sendMessageDirectToEventServer(
          this.payloadService.createThesaurusPayload(deduplicatedEntries, event.joinType, this.targetFieldName, this.appendField, this.thesaurus!),
          this.token,
          this.closePopup
        );
      } else {
        this.websocketService.sendMessageToAppServer(
          this.payloadService.createThesaurusPayload(deduplicatedEntries, event.joinType, this.targetFieldName, this.appendField, this.thesaurus!),
          this.token,
          this.closePopup
        ).subscribe((response: string) => {
          console.log("Response from App Server:", response);
        });
      }
      (event.event.target as HTMLElement)?.blur(); //Blur the button to prevent multiple clicks
    };
  }
  
  getNextPage() {
    //event.preventDefault(); //Prevent page jumping

    if (this.thesaurusService.isNextPageExists()) {
      this.termsService.incrementOffset();

      // Populate appropriate search/facet options and create detail component
      this.createThesaurusDetailComponent(this.termsService.getSelectedFacet(), this.termsService.getSearchTerm(), this.termsService.getSearchOption(), this.termsService.getOffset(), this.termsService.getSearchCategory(), this.termsService.getSearchType());

      this.enablePrevPageBtn();
    }
  }

  enablePrevPageBtn() {
    let prevPageElement = document.getElementById(this.prevPageBtnID);
    prevPageElement?.classList.remove(this.disabledClass);
  }

  disablePrevPageBtn() {
    let prevPageElement = document.getElementById(this.prevPageBtnID);
    prevPageElement?.classList.add(this.disabledClass);
  }

    getPreviousPage() {
      //event.preventDefault(); //Prevent page jumping
      if (this.termsService.getOffset() >= 50) {
        this.termsService.decrementOffset();
      }
  
      //Retrieve the previous set of 50 terms
      this.createThesaurusDetailComponent(this.termsService.getSelectedFacet(), this.termsService.getSearchTerm(), this.termsService.getSearchOption(), this.termsService.getOffset(), this.termsService.getSearchCategory(), this.termsService.getSearchType());
  
      //If the offset is 0 after retrieving, add the .disable class to the "Previous" button
      if (this.termsService.getOffset() == 0) {
        this.disablePrevPageBtn();
      }
    }
  
  private createThesaurusRootComponent() {
    if (this.thesaurusDetailComponentRef) {
      this.thesaurusDetailComponentRef.destroy();
    }

    if (this.thesaurusSingleTermComponentRef) {
      this.thesaurusSingleTermComponentRef.destroy();
    }
    this.thesaurusRootComponentRef = this.thesaurusRootContainer.createComponent(ThesaurusRootComponent);
    this.thesaurusRootComponentRef.instance.thesaurusId = this.thesaurus?.id ?? '';
    this.thesaurusRootComponentRef.instance.token = this.token;
    this.thesaurusRootComponentRef.instance.thesaurusName = this.thesaurus?.name ?? '';
    this.thesaurusRootComponentRef.instance.entryOrdinal = 0;
    this.thesaurusRootComponentRef.instance.isMajor = this.isMajor();
    this.thesaurusRootComponentRef.instance.isExplode = this.isExplode();

    this.displayPagination = false;

    this.cdr.detectChanges();
  }


  //Create instance of thesaurus-detail component programmatically, to refresh the list of terms displayed to the user
  private createThesaurusDetailComponent(facetTerm: string, searchTerm: string, searchOption: string, offset: number, searchCategory?: string, searchType?: string) {
    if (this.thesaurusSingleTermComponentRef) {
      this.thesaurusSingleTermComponentRef.destroy();
    }
    // if (this.thesaurusHierarchicalViewComponentRef) {
    //   this.thesaurusHierarchicalViewComponentRef.destroy();
    // }
    // Destroy existing instance of if it exists
    if (this.thesaurusDetailComponentRef) {
      this.thesaurusDetailComponentRef.destroy();
    }

    if(this.thesaurusRootComponentRef) {
      this.thesaurusRootComponentRef.destroy();
    }

    // Create a new instance of ThesaurusDetailComponent
    this.thesaurusDetailComponentRef = this.container.createComponent(ThesaurusDetailComponent);
    this.thesaurusDetailComponentRef.instance.thesauri = this.thesauri;
    this.thesaurusDetailComponentRef.instance.thesaurus = this.thesaurus;
    this.thesaurusDetailComponentRef.instance.token = this.token;
    this.thesaurusDetailComponentRef.instance.term = facetTerm;
    this.thesaurusDetailComponentRef.instance.searchTerm = searchTerm;
    this.thesaurusDetailComponentRef.instance.searchOption = searchOption;
    this.thesaurusDetailComponentRef.instance.searchCategory = searchCategory;
    this.thesaurusDetailComponentRef.instance.searchType = searchType;
    this.thesaurusDetailComponentRef.instance.offset = offset;

    this.displayPagination = true;

    // Manually trigger change detection
    this.cdr.detectChanges();
  }

  private createThesaurusDetailComponentFromContext() {
    if (this.thesaurusSingleTermComponentRef) {
      this.thesaurusSingleTermComponentRef.destroy();
    }
    // if (this.thesaurusHierarchicalViewComponentRef) {
    //   this.thesaurusHierarchicalViewComponentRef.destroy();
    // }
    // Destroy existing instance of if it exists
    if (this.thesaurusDetailComponentRef) {
      this.thesaurusDetailComponentRef.destroy();
    }

    if(this.thesaurusRootComponentRef) {
      this.thesaurusRootComponentRef.destroy();
    }

    // Create a new instance of ThesaurusDetailComponent
    this.thesaurusDetailComponentRef = this.container.createComponent(ThesaurusDetailComponent);
    this.thesaurusDetailComponentRef.instance.thesauri = this.thesauri;
    this.thesaurusDetailComponentRef.instance.thesaurus = this.thesaurus;
    this.thesaurusDetailComponentRef.instance.token = this.token;
    this.thesaurusDetailComponentRef.instance.term = this.detailComponentContext.browseTerm;
    this.thesaurusDetailComponentRef.instance.searchTerm = this.detailComponentContext.searchTerm;
    this.thesaurusDetailComponentRef.instance.searchOption = this.detailComponentContext.searchOption;
    this.thesaurusDetailComponentRef.instance.offset = this.detailComponentContext.offset;
    this.thesaurusDetailComponentRef.instance.searchCategory = this.detailComponentContext.searchCategory;
    this.thesaurusDetailComponentRef.instance.searchType = this.detailComponentContext.searchType;

    this.displayPagination = true;
    // Manually trigger change detection
    this.cdr.detectChanges();
  }

  setThesaurus(thesaurusId: string, availableTerm: string, token: string, selecteddatabase: string, selectedthesaurus: string): void {
    this.thesaurusService.getThesauri(token, selecteddatabase, selectedthesaurus).pipe(
        catchError(error => {
          this.thesaurusService.setThesaurusServiceError(true);
          // Handle the error (e.g., return an empty array, rethrow a specific error)
          return of([]); // Or throw new Error('Custom error');
        })
      ).subscribe(
      thesauri => {
        this.thesauri = thesauri;
        this.thesaurus = thesauri.find(thesaurus => thesaurus.id === thesaurusId);
      }
    );
  }

  getInitialMessageKeys(): string[] {
    const defaultKeys = ['initialMessage_line1', 'initialMessage_line2', 'initialMessage_line3'];

    if (this.thesaurus) {
      if (this.thesaurusService.isLexiconThesaurus(this.thesaurus.id)) {
        return defaultKeys.map(key => `acta_lexicon_qualifiers_${key}`);
      } else if (this.thesaurusService.isCorporationThesaurus(this.thesaurus.id)) {
        return defaultKeys.map(key => `company_${key}`);
      }
    }

    return defaultKeys;
  }

  showIndexSpinner() {
    this.eventsService.emitShowIndexSpinner();
    this.eventsService.emitDisableHelpButtonEvent(true);
  }

}
